"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const zod_1 = require("zod");
const manifest_1 = require("../manifest");
const _appearance_1 = require("./_appearance");
/**
 * Hero banner (SP5b) — a fully customisable header block:
 *  - Background: image (with fit: cover/contain/stretch/tile/center), OR a solid
 *    colour, OR a gradient (via the shared appearance) — and an image can also
 *    carry a gradient/solid overlay for legibility.
 *  - Text: eyebrow + headline + subtext, with size, colour, alignment.
 *  - Buttons: 0–5 button *blocks* (label/link/style/colours) — no forced button.
 * Legacy heroes (single ctaLabel/ctaHref) still render as one button.
 */
(0, manifest_1.registerSection)({
    type: 'hero', label: 'Hero banner', icon: 'image', category: 'featured',
    settingsSchema: zod_1.z.object({
        eyebrow: zod_1.z.string().max(80).default(''),
        headline: zod_1.z.string().max(300).default(''),
        subtext: zod_1.z.string().max(500).default(''),
        textSize: zod_1.z.enum(['sm', 'md', 'lg', 'xl']).default('lg'),
        minHeight: zod_1.z.enum(['sm', 'md', 'lg', 'xl']).default('md'),
        // Background image (optional). When set it sits behind the text.
        bgImage: zod_1.z.string().default(''),
        bgFit: zod_1.z.enum(['cover', 'contain', 'fill', 'tile', 'center']).default('cover'),
        overlayOpacity: zod_1.z.number().min(0).max(100).default(30),
        // Legacy single button (kept so old heroes still render).
        imageUrl: zod_1.z.string().default(''),
        ctaLabel: zod_1.z.string().max(60).default(''),
        ctaHref: zod_1.z.string().default('/category/all'),
        ..._appearance_1.appearanceShape,
    }),
    blockTypes: {
        button: { label: 'Button', icon: 'square', max: 5,
            settingsSchema: zod_1.z.object({
                label: zod_1.z.string().max(60).default('Shop now'),
                href: zod_1.z.string().default('/category/all'),
                style: zod_1.z.enum(['solid', 'outline']).default('solid'),
                color: zod_1.z.string().optional(),
                textColor: zod_1.z.string().optional(),
            }) },
    },
    maxBlocks: 5,
    defaults: () => ({
        eyebrow: '', headline: 'Welcome', subtext: '', textSize: 'lg', minHeight: 'md',
        bgImage: '', bgFit: 'cover', overlayOpacity: 30,
        imageUrl: '', ctaLabel: '', ctaHref: '/category/all',
        ...(0, _appearance_1.appearanceDefaults)(),
    }),
    fields: [
        { name: 'eyebrow', label: 'Eyebrow', control: 'text', group: 'Content' },
        { name: 'headline', label: 'Headline', control: 'text', group: 'Content' },
        { name: 'subtext', label: 'Subtext', control: 'textarea', group: 'Content' },
        { name: 'textSize', label: 'Text size', control: 'select', group: 'Content', options: [
                { label: 'Small', value: 'sm' }, { label: 'Medium', value: 'md' }, { label: 'Large', value: 'lg' }, { label: 'Extra large', value: 'xl' },
            ] },
        { name: 'minHeight', label: 'Height', control: 'select', group: 'Content', options: [
                { label: 'Short', value: 'sm' }, { label: 'Medium', value: 'md' }, { label: 'Tall', value: 'lg' }, { label: 'Full-ish', value: 'xl' },
            ] },
        // Background image group
        { name: 'bgImage', label: 'Background image', control: 'image', group: 'Background image' },
        { name: 'bgFit', label: 'Image fit', control: 'select', group: 'Background image', options: [
                { label: 'Cover (fill & crop)', value: 'cover' }, { label: 'Contain', value: 'contain' },
                { label: 'Stretch', value: 'fill' }, { label: 'Tile', value: 'tile' }, { label: 'Center', value: 'center' },
            ] },
        { name: 'overlayOpacity', label: 'Overlay darkness (%)', control: 'number', min: 0, max: 100, group: 'Background image' },
        ..._appearance_1.appearanceFields,
    ],
    blockFields: {
        button: [
            { name: 'label', label: 'Label', control: 'text' },
            { name: 'href', label: 'Link URL', control: 'url' },
            { name: 'style', label: 'Style', control: 'select', options: [{ label: 'Solid', value: 'solid' }, { label: 'Outline', value: 'outline' }] },
            { name: 'color', label: 'Button colour', control: 'color' },
            { name: 'textColor', label: 'Text colour', control: 'color' },
        ],
    },
});
//# sourceMappingURL=hero.js.map